import React from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faEye } from '@fortawesome/pro-light-svg-icons/faEye';
import { faHeadphones } from '@fortawesome/pro-light-svg-icons/faHeadphones';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faReply } from '@fortawesome/pro-light-svg-icons/faReply';
import { faStar } from '@fortawesome/pro-light-svg-icons/faStar';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faPen } from '@fortawesome/pro-light-svg-icons/faPen';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { TARGET_KIND_LABEL } from '../outreachLabels';
import { serviceBrand } from '../outreachServiceBrand';
import { compactCount } from '../outreachFormat';
import { outreachFindPath, outreachReplyPath, outreachReviewPath } from '../outreachPaths';
import type { OutreachActivityEvent, OutreachActivityKind } from '../outreachTypes';
import { replyLabelText } from './replyLabelText';
import { activityMessages as m } from './activityMessages';
import { liveMessages } from './liveMessages';
import { activityTime, listenDuration } from './liveFormat';
import { knownKind } from './SearchMatchRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the live screens; each part uses its own slice of it */
import styles from './Live.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ICON: Record<OutreachActivityKind, { icon: IconDefinition; className: string | undefined }> = {
  sent: { icon: faPaperPlane, className: undefined },
  followUp: { icon: faPaperPlane, className: undefined },
  opened: { icon: faEye, className: styles.iconOpen },
  listened: { icon: faHeadphones, className: styles.iconListen },
  clicked: { icon: faArrowUpRightFromSquare, className: styles.iconOpen },
  replied: { icon: faReply, className: styles.iconReply },
  placed: { icon: faStar, className: styles.iconPlace },
  found: { icon: faMagnifyingGlass, className: styles.iconFound },
  ready: { icon: faPen, className: styles.iconFound },
};

/** The line of one event, composed from its kind and its fields (the API sends no text). */
export function activityText(intl: IntlShape, event: OutreachActivityEvent): React.ReactNode {
  const who = <b key="who">{event.who?.name ?? intl.formatMessage(m.someone)}</b>;
  const song = event.songTitle ?? intl.formatMessage(m.yourSong);
  const count = event.count ?? 0;
  switch (event.kind) {
    case 'sent':
      return intl.formatMessage(m.sent, { who });
    case 'followUp':
      return intl.formatMessage(m.followUp, { who });
    case 'opened':
      return intl.formatMessage(m.opened, { who });
    case 'listened':
      return intl.formatMessage(m.listened, { who, song, duration: listenDuration(intl, event.listenedSeconds ?? 0) });
    case 'clicked':
      return intl.formatMessage(m.clicked, { who, song, service: serviceBrand(event.service ?? '').name });
    case 'replied':
      return intl.formatMessage(m.replied, { who, song });
    case 'placed':
      return event.count === null
        ? intl.formatMessage(m.placed, { who, song })
        : intl.formatMessage(m.placedAt, { who, song, position: event.count });
    case 'found':
      return intl.formatMessage(m.found, { count, song });
    case 'ready':
      return intl.formatMessage(m.ready, { count, song });
    default:
      return null;
  }
}

/** The grey line under the event: who the curator is, or the reply label and its first words. */
export function activityMeta(intl: IntlShape, event: OutreachActivityEvent): string {
  if (event.kind === 'found') {
    return intl.formatMessage(m.foundMeta);
  }
  if (event.kind === 'ready') {
    return intl.formatMessage(m.readyMeta);
  }
  const parts: string[] = [];
  if (event.kind === 'replied') {
    if (event.replyLabel !== null) {
      parts.push(intl.formatMessage(replyLabelText(event.replyLabel)));
    }
    if (event.snippet !== null && event.snippet !== '') {
      parts.push(intl.formatMessage(liveMessages.quoted, { text: event.snippet }));
    }
    return parts.join(' · ');
  }
  if (event.who?.kind !== null && event.who?.kind !== undefined) {
    parts.push(intl.formatMessage(TARGET_KIND_LABEL[knownKind(event.who.kind)]));
  }
  if (event.who?.followers !== null && event.who?.followers !== undefined) {
    parts.push(intl.formatMessage(m.followers, { count: compactCount(intl, event.who.followers) }));
  }
  return parts.join(' · ');
}

/** The path of the event's one action: answer a reply, see the new curators, read the pitches. */
function actionOf(event: OutreachActivityEvent): { label: typeof m.answer; path: string } | null {
  if (event.kind === 'replied' && event.replyUuid !== null) {
    return { label: m.answer, path: outreachReplyPath(event.replyUuid) };
  }
  if (event.kind === 'found') {
    return { label: m.seeThem, path: outreachFindPath() };
  }
  if (event.kind === 'ready' && event.campaignUuid !== null) {
    return { label: m.readThem, path: outreachReviewPath(event.campaignUuid) };
  }
  return null;
}

/** One line of the timeline (design t2, t3). A reply is the highlighted line. */
const ActivityRowComponent: React.FC<{ event: OutreachActivityEvent }> = ({ event }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const icon = ICON[event.kind] ?? ICON.sent;
  const action = actionOf(event);
  const meta = activityMeta(intl, event);
  return (
    <li
      className={clsx(styles.event, event.kind === 'replied' && styles.eventHighlight)}
      data-testid="activity-row"
      data-kind={event.kind}
    >
      <span className={clsx(styles.eventIcon, icon.className)} aria-hidden>
        <FontAwesomeIcon icon={icon.icon} />
      </span>
      <div className={styles.eventText}>
        <div>{activityText(intl, event)}</div>
        {meta !== '' && <div className={styles.eventMeta}>{meta}</div>}
      </div>
      {action !== null && (
        // eslint-disable-next-line no-void
        <SMLButton size="sm" outline omitCap onClick={() => void navigate(action.path)}>
          {intl.formatMessage(action.label)}
        </SMLButton>
      )}
      <time className={styles.eventTime} dateTime={event.at}>
        {activityTime(intl, event.at)}
      </time>
    </li>
  );
};

export const ActivityRow = TypedMemo(ActivityRowComponent);
